// councilAttachments.js — Council-LOCAL attachment serialization (PURE).
//
// WHY a local serializer instead of the global hook helper: the shared
// `serializeAttachmentsWithContent` interpolates `a.name` raw into an XML-ish
// `name="..."` attribute, so a hostile filename can break out of the tag and
// become free text in the prompt sent to every council agent (security-design
// H3/P1). Changing the global helper would touch every composer; this module
// owns the boundary for Council only and escapes attribute-bound values.
//
// It also enforces the CONTEXT truthfulness rule: only text that was ACTUALLY
// read is inlined, truncation is visible, and non-text attachments are
// metadata-only with no claim that their content was analyzed.

export const MAX_TEXT_CONTENT_CHARS = 30000;

/** Escape a value that will sit inside a double-quoted XML-ish attribute. */
export function escapeAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * True when an attachment carries text that was genuinely read from disk.
 * Binary/image attachments have `textContent == null` and are never inlined.
 */
export function isInlinedText(attachment) {
  return attachment != null && typeof attachment.textContent === 'string' && attachment.textContent.length > 0;
}

/**
 * Serialize one attachment to a prompt block. Text (read) → inline body with a
 * visible truncation marker; everything else → metadata only.
 * @param {object} a
 * @returns {{block: string, truncated: boolean, inlined: boolean}}
 */
export function serializeAttachment(a) {
  const name = escapeAttr(a?.name || 'arquivo');
  const sizeKb = ((a?.size || 0) / 1024).toFixed(1);
  const typeAttr = a?.type ? ` type="${escapeAttr(a.type)}"` : '';
  if (!isInlinedText(a)) {
    return {
      block: `<file name="${name}" size="${sizeKb}KB"${typeAttr} content="binary, não inlinable" />`,
      truncated: false,
      inlined: false,
    };
  }
  const truncated = a.textContent.length > MAX_TEXT_CONTENT_CHARS;
  const body = truncated
    ? `${a.textContent.slice(0, MAX_TEXT_CONTENT_CHARS)}\n\n[…truncado]`
    : a.textContent;
  return {
    block: `<file name="${name}" size="${sizeKb}KB"${typeAttr}>\n${body}\n</file>`,
    truncated,
    inlined: true,
  };
}

/**
 * Build the prompt-embedding for a Council question + attachments. The query is
 * left untouched (it is the public question); attachment blocks are appended.
 * @param {string} query
 * @param {object[]} attachments
 * @returns {string}
 */
export function serializeCouncilPrompt(query, attachments) {
  if (!Array.isArray(attachments) || attachments.length === 0) return query;
  const blocks = attachments.map((a) => serializeAttachment(a).block);
  return `${query}\n\n${blocks.join('\n\n')}`;
}

/**
 * Human-facing attachment descriptors for the composer chips. Mirrors what will
 * actually reach the model so the UI can show truncation/limitations honestly.
 * @param {object[]} attachments
 * @returns {{id, name, kind, sizeKb, inlined, truncated, note}[]}
 */
export function describeAttachments(attachments) {
  return (Array.isArray(attachments) ? attachments : []).map((a) => {
    const text = isInlinedText(a);
    const truncated = text && a.textContent.length > MAX_TEXT_CONTENT_CHARS;
    const note = text
      ? (truncated ? 'Conteúdo lido (truncado)' : 'Conteúdo lido')
      : 'Apenas metadados — conteúdo não analisado';
    return {
      id: a.id,
      name: a.name,
      kind: a.kind || (text ? 'text' : 'binary'),
      sizeKb: (a.size || 0) / 1024,
      inlined: text,
      truncated,
      note,
    };
  });
}
